import type {BarElement, BarProps, Chart, ChartType, Plugin} from 'chart.js';
import type {Context as ChartDataLabelsContext} from 'chartjs-plugin-datalabels';

import {createBarChartLabelFormatterContext} from './chartjsBarLabelContext';

/**
 * @see https://chartjs-plugin-datalabels.netlify.app/guide/options.html#formatter
 */
export type BarLeftCircleLabelsFormatter = (value: number, context: ChartDataLabelsContext) => string | null;

export type BarLeftCircleLabelsOptions = {
    overlapOffset?: number;
    gap?: number;
    minRadius?: number;
    textPadding?: number;
    backgroundColor?: string;
    borderColor?: string;
    borderWidth?: number;
    color?: string;
    font?: {
        size?: number;
        weight?: string | number;
        family?: string;
    };
    formatter?: BarLeftCircleLabelsFormatter;
};

declare module 'chart.js' {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- declaration merge
    interface PluginOptionsByType<TType extends ChartType> {
        barLeftCircleLabels?: BarLeftCircleLabelsOptions;
    }
}

type ResolvedBarLeftCircleLabels = Required<
    Pick<
        BarLeftCircleLabelsOptions,
        'overlapOffset' | 'gap' | 'minRadius' | 'textPadding' | 'backgroundColor' | 'borderColor' | 'borderWidth' | 'color'
    >
> & {
    font: NonNullable<BarLeftCircleLabelsOptions['font']>;
    formatter: NonNullable<BarLeftCircleLabelsOptions['formatter']>;
};

const defaultOptions: ResolvedBarLeftCircleLabels = {
    overlapOffset: 25,
    gap: 6,
    minRadius: 16,
    textPadding: 6,
    backgroundColor: '#ffffff',
    borderColor: '#EB73DE',
    borderWidth: 2,
    color: '#EB73DE',
    font: {
        size: 13,
        weight: 'bold',
        family: 'Moderat',
    },
    formatter: (value: number, _context: ChartDataLabelsContext) => value.toString(),
};

function resolveOptions(options: BarLeftCircleLabelsOptions | undefined) {
    return {
        ...defaultOptions,
        ...options,
        font: {...defaultOptions.font, ...options?.font},
        formatter: options?.formatter ?? defaultOptions.formatter,
    };
}

export const barLeftCircleLabelsPlugin: Plugin<'bar', BarLeftCircleLabelsOptions> = {
    id: 'barLeftCircleLabels',

    afterDatasetsDraw(chart: Chart<'bar'>, _args: unknown, pluginOptions: BarLeftCircleLabelsOptions | undefined) {
        const options = resolveOptions(pluginOptions);
        const {ctx} = chart;

        ctx.save();

        for (let datasetIndex = 0; datasetIndex < chart.data.datasets.length; datasetIndex++) {
            const dataset = chart.data.datasets[datasetIndex];
            const meta = chart.getDatasetMeta(datasetIndex);
            if (meta.hidden) {
                continue;
            }

            const data = dataset.data as number[];

            for (let dataIndex = 0; dataIndex < data.length; dataIndex++) {
                const raw = data[dataIndex];
                if (raw == null || Number.isNaN(Number(raw))) {
                    continue;
                }

                const element = meta.data[dataIndex] as (BarElement & BarProps) | undefined;
                if (!element) {
                    continue;
                }

                // Designed for `indexAxis: 'y'` (horizontal bars): label sits to the left of the bar start.
                if (!element.horizontal) {
                    continue;
                }

                const value = Number(raw);
                const formatterContext = createBarChartLabelFormatterContext(chart, datasetIndex, dataIndex);
                const formatted = options.formatter(value, formatterContext);
                if (formatted == null) {
                    continue;
                }
                const text = String(formatted);
                const barLeft = Math.min(element.x, element.base);
                const centerY = element.y;

                const fontSize = options.font.size ?? 13;
                const fontFamily = options.font.family ?? 'sans-serif';
                const fontWeight = options.font.weight ?? 'bold';
                ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;

                const metrics = ctx.measureText(text);
                const textWidth = metrics.width;
                const textHeight = fontSize;
                const radiusFromText = Math.hypot(textWidth / 2, textHeight / 2) + options.textPadding;
                const minRadiusFromBar = element.height / 2;
                const radius = Math.max(options.minRadius, radiusFromText, minRadiusFromBar);

                let centerX = barLeft - radius + options.overlapOffset;
                const minX = radius + options.borderWidth;
                if (centerX < minX) {
                    centerX = minX;
                }

                ctx.beginPath();
                ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
                ctx.fillStyle = options.backgroundColor;
                ctx.fill();
                ctx.strokeStyle = options.borderColor;
                ctx.lineWidth = options.borderWidth;
                ctx.stroke();

                ctx.fillStyle = options.color;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(text, centerX, centerY);
            }
        }

        ctx.restore();
    },
};
